import { inject, Injectable, signal, Signal } from '@angular/core';
import { Barcode, BarcodeFormat, BarcodeScanner, BarcodesScannedEvent } from '@capacitor-mlkit/barcode-scanning';
import { PluginListenerHandle } from '@capacitor/core';
import { Torch } from '@capawesome/capacitor-torch';
import { CapacitorService } from '@libs/shared/common';
import { interval, Observable, Subject, Subscription } from 'rxjs';
/* this is work in progress and will be tested later on */

/* istanbul ignore file */

@Injectable({
  providedIn: 'root',
})
export class MlKitScannerService {
  private currentListenerInstance: PluginListenerHandle | undefined = undefined;
  private currentEventHandler: Subject<BarcodesScannedEvent> | undefined = undefined;
  private eventSubscription: Subscription | undefined = undefined;
  private tickInterval: Subscription | undefined = undefined;
  private readonly barcodeMap: Map<string, BarcodesScannedEvent> = new Map<string, BarcodesScannedEvent>();
  private readonly boxes: Map<string, HTMLDivElement> = new Map<string, HTMLDivElement>();
  private readonly boxLastAccessed: Map<string, number> = new Map<string, number>();
  private sightDetectionCornerPoints: number[][] | undefined = undefined;
  private readonly capacitorService: CapacitorService = inject(CapacitorService);
  private readonly formats: BarcodeFormat[] = [BarcodeFormat.Itf, BarcodeFormat.Code39, BarcodeFormat.Code128, BarcodeFormat.Ean8, BarcodeFormat.Ean13];
  private scanValue: Subject<string> = new Subject<string>();
  private continuousScan: Signal<boolean> = signal(false);
  private scanProcessing: Signal<boolean> = signal(false);

  public async Scan(sight: HTMLElement | undefined = undefined, _continuousScan?: Signal<boolean>, _scanProcessing?: Signal<boolean>): Promise<Observable<string>> {
    this.scanValue = new Subject<string>();

    if (_continuousScan) {
      this.continuousScan = _continuousScan;
    }

    if (_scanProcessing) {
      this.scanProcessing = _scanProcessing;
    }

    await this.StartScan(sight);

    return this.scanValue.asObservable();
  }

  public MultiScan(element: HTMLElement): Promise<string> {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises,no-async-promise-executor
    return new Promise<string>(async resolve => {
      const scannerEvents$: Observable<BarcodesScannedEvent> = await this.InitMultiScan();

      this.eventSubscription = scannerEvents$.subscribe((result: BarcodesScannedEvent) => {
        for (const barcode of result.barcodes) {
          const parsedBarcode: string = this.parseScannedCode(barcode);

          this.barcodeMap.set(parsedBarcode, result);
          this.boxLastAccessed.set(parsedBarcode, Date.now());
        }
      });

      this.tickInterval = interval(500).subscribe(() => {
        const barcodes: [string, BarcodesScannedEvent][] = Array.from(this.barcodeMap.entries());

        for (const barcode of barcodes) {
          const key: string = barcode[0];

          if (this.ClearNonVisibleBarcode(key, element)) {
            continue;
          }

          this.CalculateAndSetBoxForBarcode(barcode[1], element, resolve);
        }
      });
    });
  }

  public async StopMultiScan(): Promise<void> {
    if (this.currentListenerInstance) {
      await this.currentListenerInstance.remove();
    }

    if (this.currentEventHandler) {
      this.currentEventHandler.complete();
    }

    if (this.eventSubscription) {
      this.eventSubscription.unsubscribe();
    }

    if (this.tickInterval) {
      this.tickInterval.unsubscribe();
    }

    this.currentListenerInstance = undefined;
    this.currentEventHandler = undefined;
    this.tickInterval = undefined;
    this.eventSubscription = undefined;
    this.barcodeMap.clear();
    this.boxes.clear();
    this.boxLastAccessed.clear();

    await this.StopScan();
  }

  public async IsSupported(): Promise<boolean> {
    const { supported } = await BarcodeScanner.isSupported();

    return supported;
  }

  public async EnableTorch(): Promise<void> {
    await Torch.enable();
  }

  public async DisableTorch(): Promise<void> {
    await Torch.disable();
  }

  public async ToggleTorch(): Promise<void> {
    await Torch.toggle();
  }

  public async IsTorchEnabled(): Promise<boolean> {
    const { enabled } = await Torch.isEnabled();

    return enabled;
  }

  public async IsTorchAvailable(): Promise<boolean> {
    const { available } = await Torch.isAvailable();

    return available;
  }

  public async SetZoomRatio(): Promise<void> {
    await BarcodeScanner.setZoomRatio({ zoomRatio: 0.5 });
  }

  public async GetZoomRatio(): Promise<number> {
    const { zoomRatio } = await BarcodeScanner.getZoomRatio();

    return zoomRatio;
  }

  public async GetMinZoomRatio(): Promise<number> {
    const { zoomRatio } = await BarcodeScanner.getMinZoomRatio();

    return zoomRatio;
  }

  public async GetMaxZoomRatio(): Promise<number> {
    const { zoomRatio } = await BarcodeScanner.getMaxZoomRatio();

    return zoomRatio;
  }

  public async OpenSettings(): Promise<void> {
    await BarcodeScanner.openSettings();
  }

  public async IsGoogleBarcodeScannerModuleAvailable(): Promise<boolean> {
    const { available } = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();

    return available;
  }

  public async InstallGoogleBarcodeScannerModule(): Promise<void> {
    await BarcodeScanner.installGoogleBarcodeScannerModule();
  }

  public async checkPermissions(): Promise<boolean> {
    const { camera } = await BarcodeScanner.checkPermissions();

    return camera !== 'denied';
  }

  public async RequestPermissions(): Promise<boolean> {
    const { camera } = await BarcodeScanner.requestPermissions();

    return camera !== 'denied';
  }

  public async StopScan(): Promise<void> {
    // Make all elements in the WebView visible again
    document.querySelector('body')?.classList.remove('barcode-scanner-active');

    // Remove all listeners
    await BarcodeScanner.removeAllListeners();

    // Stop the barcode scanner
    await BarcodeScanner.stopScan();
  }

  public CalculateSightCornerDetectionPoints(sight: HTMLElement | undefined = undefined): void {
    const squareElementBoundingClientRect: DOMRect | undefined = sight?.getBoundingClientRect();
    const pixelRatio: number = window.devicePixelRatio;
    // https://capawesome.io/plugins/mlkit/barcode-scanning/#barcode
    const scaledRect: {
      top: number;
      left: number;
      bottom: number;
      width: number;
      right: number;
      height: number;
    } | undefined
      = squareElementBoundingClientRect
      ? {
        left: squareElementBoundingClientRect.left * pixelRatio,
        right:
          squareElementBoundingClientRect.right * pixelRatio,
        top: squareElementBoundingClientRect.top * pixelRatio,
        bottom:
          squareElementBoundingClientRect.bottom * pixelRatio,
        width:
          squareElementBoundingClientRect.width * pixelRatio,
        height:
          squareElementBoundingClientRect.height * pixelRatio,
      }
      : undefined;

    this.sightDetectionCornerPoints = scaledRect
      ? [
        [scaledRect.left, scaledRect.top],
        [scaledRect.left + scaledRect.width, scaledRect.top],
        [
          scaledRect.left + scaledRect.width,
          scaledRect.top + scaledRect.height,
        ],
        [scaledRect.left, scaledRect.top + scaledRect.height],
      ]
      : undefined;
  }

  private async Init(): Promise<void> {
    if (!await this.IsSupported()) {
      throw new Error('Barcode scanning is not supported on this device');
    }

    if (!await this.checkPermissions()) {
      if (!await this.RequestPermissions()) {
        throw new Error('Camera permissions are required to use the barcode scanner');
      }
    }

    // care even calling IsGoogleBarcodeScannerModuleAvailable causes ios to break
    if (this.capacitorService.isRunningOnAndroid() && !await this.IsGoogleBarcodeScannerModuleAvailable()) {
      await this.InstallGoogleBarcodeScannerModule();
    }
  }

  private async InitMultiScan(): Promise<Observable<BarcodesScannedEvent>> {
    await this.Init();
    await this.StopMultiScan();

    this.currentEventHandler = new Subject<BarcodesScannedEvent>();

    this.currentListenerInstance = await BarcodeScanner.addListener(
      'barcodesScanned',
      result => {
        this.currentEventHandler?.next(result);
      },
    );

    // we can decide what we want here
    document.querySelector('body')?.classList.add('barcode-scanner-active');
    // Start the barcode scanner
    await BarcodeScanner.startScan({ formats: this.formats });

    return this.currentEventHandler.asObservable();
  }

  private async StartScan(sight: HTMLElement | undefined = undefined): Promise<void> {
    await this.Init();

    this.CalculateSightCornerDetectionPoints(sight);

    let results: string[] = [];

    const listener: PluginListenerHandle = await BarcodeScanner.addListener(
      'barcodesScanned',
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      async result => {
        if (this.scanProcessing()) {
          results = [];

          return;
        }

        for (const barcode of result.barcodes) {
          // https://capawesome.io/plugins/mlkit/barcode-scanning/#barcode
          // https://github.com/robingenz/capacitor-mlkit-plugin-demo/blob/main/src/app/modules/barcode-scanning/barcode-scanning-modal.component.ts
          const cornerPoints: [[number, number], [number, number], [number, number], [number, number]] | undefined = barcode.cornerPoints;

          if (this.sightDetectionCornerPoints && cornerPoints) {
            const detectionCornerPoints: number[][] = this.sightDetectionCornerPoints;

            if (
              detectionCornerPoints[0][0] > cornerPoints[0][0] ||
              detectionCornerPoints[0][1] > cornerPoints[0][1] ||
              detectionCornerPoints[1][0] < cornerPoints[1][0] ||
              detectionCornerPoints[1][1] > cornerPoints[1][1] ||
              detectionCornerPoints[2][0] < cornerPoints[2][0] ||
              detectionCornerPoints[2][1] < cornerPoints[2][1] ||
              detectionCornerPoints[3][0] > cornerPoints[3][0] ||
              detectionCornerPoints[3][1] < cornerPoints[3][1]
            ) {
              continue;
            }
          }

          results.push(this.parseScannedCode(barcode));
        }

        // we had some issues with scanner scanning wrong codes.
        // this gets 5 codes and uses the one which was scanned the most.
        // this decreases performance a little bit but in my experience
        // is nearly 99% accurate.
        if (results.length >= 5) {
          if (!this.continuousScan()) {
            await listener.remove();
            await this.StopScan();
          }

          const countMap: Record<string, number> = {};

          for (const code of results) {
            if (!countMap[code]) {
              countMap[code] = 1;
            } else {
              countMap[code] = countMap[code] + 1;
            }
          }

          const ordered: [string, number][] = Object.entries(countMap).sort((a, b) => b[1] - a[1]);

          results = [];
          this.scanValue.next(ordered[0][0]);
        }
      },
    );

    // we can decide what we want here
    document.querySelector('body')?.classList.add('barcode-scanner-active');

    // Start the barcode scanner
    await BarcodeScanner.startScan({ formats: this.formats });
  }

  private CalculateAndSetBoxForBarcode(result: BarcodesScannedEvent, element: HTMLElement, resolve: (value: (string | PromiseLike<string>)) => void): void {
    for (const barcode of result.barcodes) {
      if (!barcode.cornerPoints) {
        return;
      }

      const topLeft: [number, number] = barcode.cornerPoints[0];
      const bottomRight: [number, number] = barcode.cornerPoints[2];
      const bottomLeft: [number, number] = barcode.cornerPoints[3];

      const right: number = bottomRight[0] / window.devicePixelRatio;
      const bottom: number = bottomLeft[1] / window.devicePixelRatio;
      const left: number = topLeft[0] / window.devicePixelRatio;
      const top: number = topLeft[1] / window.devicePixelRatio;
      const width: number = right - left;
      const height: number = bottom - top;

      let isNew: boolean = true;
      let box: HTMLDivElement;

      if (this.boxes.has(barcode.displayValue)) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        box = this.boxes.get(barcode.displayValue)!;

        if (!box) {
          return;
        }

        element.appendChild(box);
        isNew = false;
      } else {
        box = document.createElement('div');
        box.classList.add('scanner-box');
        box.onclick = async (): Promise<void> => {
          await this.StopMultiScan();
          resolve(barcode.displayValue);
        };
        this.boxes.set(barcode.displayValue, box);
      }

      const rects: DOMRect = box.getBoundingClientRect();
      const threshold: number = 50;

      if (!isNew && rects.left - left <= threshold && rects.top - top <= threshold && rects.width - width <= threshold && rects.height - height <= threshold) {
        return;
      }

      box.style.left = `${left}px`;
      box.style.top = `${top}px`;
      box.style.width = `${width}px`;
      box.style.height = `${height}px`;
      box.style.position = 'absolute';
      box.innerHTML = barcode.displayValue;
    }
  }

  private ClearNonVisibleBarcode(key: string, element: HTMLElement): boolean {
    const lastAccessed: number | undefined = this.boxLastAccessed.get(key);

    if (lastAccessed) {
      try {
        if (Date.now() - lastAccessed > 500) {
          this.barcodeMap.delete(key);
          this.boxLastAccessed.delete(key);
          const box: HTMLDivElement | undefined = this.boxes.get(key);

          if (box) {
            element.removeChild(box);
            this.boxes.delete(key);
          }

          return true;
        }
      } catch {
        return true;
      }
    }

    return false;
  }

  private parseScannedCode(barcode: Barcode): string {
    // this solves the issue that gtin 14 barcodes often have leading "(01)" on their barcode.
    // the scanner adds the leading 01 to the barcode which causes the gtin 14 to be incorrect.
    // remove the leading 01 if the below criteria is met
    const barcodeValue: string = barcode.displayValue;

    if (barcodeValue.length === 16 && barcodeValue.startsWith('01')) {
      return barcodeValue.slice(2);
    }

    return barcodeValue;
  }
}
